/**
 * Audit Log Panel Component
 *
 * A slide-out drawer that displays paginated audit log records for a view.
 * Shows timestamped entries with color-coded action chips and field diffs.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Button,
  Sheet,
  Stack,
} from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";

interface AuditRecord {
  id: string;
  view_id: string;
  user_id: string;
  action: "INSERT" | "UPDATE" | "DELETE";
  row_id: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  accessed_at: string;
}

interface AuditResponse {
  items: AuditRecord[];
  total: number;
  page: number;
  page_size: number;
}

interface AuditLogPanelProps {
  open: boolean;
  onClose: () => void;
  viewId: string;
}

const ACTION_COLORS: Record<string, "success" | "primary" | "danger"> = {
  INSERT: "success",
  UPDATE: "primary",
  DELETE: "danger",
};

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Renders the changed fields diff for UPDATE actions.
 * Shows old value -> new value for each changed field.
 */
function ChangedFieldsDiff({
  oldValues,
  newValues,
}: {
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
}) {
  if (!oldValues && !newValues) return null;

  const allKeys = new Set([
    ...Object.keys(oldValues || {}),
    ...Object.keys(newValues || {}),
  ]);

  const changedKeys = Array.from(allKeys).filter((key) => {
    const oldVal = oldValues?.[key];
    const newVal = newValues?.[key];
    return JSON.stringify(oldVal) !== JSON.stringify(newVal);
  });

  if (changedKeys.length === 0) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      {changedKeys.map((key) => (
        <Box
          key={key}
          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.25 }}
        >
          <Typography
            level="body-xs"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            {key}:
          </Typography>
          <Typography
            level="body-xs"
            sx={{ textDecoration: "line-through", color: "danger.500" }}
          >
            {formatValue(oldValues?.[key])}
          </Typography>
          <Typography level="body-xs" sx={{ color: "text.secondary" }}>
            {" -> "}
          </Typography>
          <Typography level="body-xs" sx={{ color: "success.700" }}>
            {formatValue(newValues?.[key])}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "(empty)";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export default function AuditLogPanel({
  open,
  onClose,
  viewId,
}: AuditLogPanelProps) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchAuditLogs = useCallback(async () => {
    if (!viewId || !workspaceId) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      const endpoint = `/api/workspace/myvault/view-rows/${viewId}/audit?${params.toString()}`;
      const data: AuditResponse = await authFetch(endpoint, "audit-log-fetch", {
        headers: { "x-workspace-id": workspaceId },
      });
      setRecords(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
      setError("Failed to load audit logs.");
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [authFetch, viewId, workspaceId, page, pageSize]);

  useEffect(() => {
    if (open) {
      fetchAuditLogs();
    }
  }, [open, fetchAuditLogs]);

  // Reset page when panel opens
  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      size="md"
      slotProps={{
        content: {
          sx: {
            width: { xs: "100%", sm: 480 },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography level="title-lg">Audit Log</Typography>
        <IconButton variant="plain" color="neutral" size="sm" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size="md" />
          </Box>
        )}

        {error && (
          <Typography level="body-sm" color="danger" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && records.length === 0 && (
          <Typography
            level="body-sm"
            sx={{ color: "text.tertiary", py: 4, textAlign: "center" }}
          >
            No audit records found for this view.
          </Typography>
        )}

        {!loading && !error && records.length > 0 && (
          <Stack spacing={1}>
            {records.map((record) => (
              <Sheet
                key={record.id}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: "sm" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={ACTION_COLORS[record.action] || "neutral"}
                    >
                      {record.action}
                    </Chip>
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.secondary" }}
                    >
                      Row: {record.row_id}
                    </Typography>
                  </Box>
                  <Typography
                    level="body-xs"
                    sx={{ color: "text.tertiary", whiteSpace: "nowrap" }}
                  >
                    {formatTimestamp(record.accessed_at)}
                  </Typography>
                </Box>

                {record.user_id && (
                  <Typography
                    level="body-xs"
                    sx={{ color: "text.secondary", mb: 0.5 }}
                  >
                    User: {record.user_id}
                  </Typography>
                )}

                {record.action === "UPDATE" && (
                  <ChangedFieldsDiff
                    oldValues={record.old_values}
                    newValues={record.new_values}
                  />
                )}

                {record.action === "INSERT" && record.new_values && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.secondary" }}
                    >
                      {Object.keys(record.new_values).length} field(s) set
                    </Typography>
                  </Box>
                )}

                {record.action === "DELETE" && record.old_values && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.secondary" }}
                    >
                      {Object.keys(record.old_values).length} field(s) removed
                    </Typography>
                  </Box>
                )}
              </Sheet>
            ))}
          </Stack>
        )}
      </Box>

      {/* Pagination Footer */}
      {total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography level="body-xs" sx={{ color: "text.secondary" }}>
            {total} record{total !== 1 ? "s" : ""} total
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              color="neutral"
              size="sm"
              startDecorator={<NavigateBeforeIcon />}
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Typography level="body-xs" sx={{ color: "text.secondary" }}>
              Page {page} of {totalPages}
            </Typography>
            <Button
              variant="outlined"
              color="neutral"
              size="sm"
              endDecorator={<NavigateNextIcon />}
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
